import { ROLES } from "../config/constants";
import { formatDate } from "./timeHelpers";
import { calculatePeriodStats, escapeHtml, processLogsWithDuplicates } from "./attendanceStats";

export function requireReportAdmin(role) {
  if (role !== ROLES.ADMIN) throw new Error("僅管理員可匯出報表");
}

export function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function getReportMonths(start, end) {
  const parse = value => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value || "")) throw new Error("請選擇完整的起訖月份");
    const [year, month] = value.split("-").map(Number);
    if (year < 1900 || year > 9998) throw new Error("年份不在有效範圍內");
    return year * 12 + month - 1;
  };
  const first = parse(start), last = parse(end);
  if (first > last) throw new Error("開始月份不能晚於結束月份");
  if (last - first >= 120) throw new Error("每次最多匯出 120 個月，請分段匯出");
  return Array.from({ length: last - first + 1 }, (_, i) => {
    const year = Math.floor((first + i) / 12), month = (first + i) % 12;
    return { year, month, key: `${year}-${String(month + 1).padStart(2, "0")}` };
  });
}

export function buildAttendanceReport(datasets, months) {
  let count = 0;
  const missing = [];
  const sections = [];
  for (const person of datasets) {
    const logs = processLogsWithDuplicates(person.logs);
    for (const { year, month, key } of months) {
      const stats = calculatePeriodStats(person.uid, logs, person.leaves, person.role, year, month);
      const entries = Object.entries(stats.dailyStats).sort(([a], [b]) => a.localeCompare(b));
      if (!entries.length) missing.push(`${person.name} ${key}`);
      count += entries.length;
      const rows = entries.map(([date, day]) => {
        const states = [];
        if (day.isAbnormal) states.push("異常(缺卡)");
        if (day.isLate) states.push(`遲到 ${day.lateMinutes} 分鐘`);
        return `<tr><td>${escapeHtml(date)}</td><td>${day.rawHtml}</td><td>${escapeHtml(day.ranges)}</td><td>${day.hours}</td><td>${escapeHtml(states.join("；") || "正常")}</td></tr>`;
      }).join("");
      sections.push(`<section><h2>${escapeHtml(person.name)} · ${key}</h2><p>總工時：${stats.totalHours} 小時 · 遲到：${stats.totalLateMinutes} 分鐘</p><table><thead><tr><th>日期</th><th>打卡紀錄</th><th>有效區間</th><th>工時</th><th>狀態</th></tr></thead><tbody>${rows || '<tr><td colspan="5">此月無出勤紀錄</td></tr>'}</tbody></table></section>`);
    }
  }
  if (!count) throw new Error("所選人員與月份區間沒有出勤紀錄");
  const range = `${months[0].key} ~ ${months[months.length - 1].key}`;
  return {
    filename: `Attendance_${months[0].key}_${months[months.length - 1].key}.html`,
    mime: "text/html;charset=utf-8",
    content: `<!doctype html><html lang="zh-Hant"><head><meta charset="utf-8"><title>出勤明細 ${range}</title><style>body{font-family:Arial,"Microsoft JhengHei",sans-serif;padding:24px;color:#222}table{border-collapse:collapse;width:100%;margin-bottom:24px}th,td{border:1px solid #bbb;padding:8px;text-align:left}th{background:#eee}h2{font-size:18px;border-bottom:2px solid #555;padding-bottom:8px}section{break-inside:avoid}h1{font-size:24px}@media print{body{padding:0}thead{display:table-header-group}}</style></head><body><h1>出勤明細 ${range}</h1><p>匯出日期：${formatDate(new Date())}</p>${sections.join("")}</body></html>`,
    message: `已匯出 ${datasets.length} 位人員、${count} 筆每日明細`,
    missing,
  };
}

const number = value => Number(value) || 0;
const sum = items => (items || []).reduce((total, item) => total + number(item.amount), 0);
const details = items => (items || []).map(item => `${item.name || item.typeName || item.type || "未命名"}：${number(item.amount)}`).join("；");
export function csvCell(value) {
  let text = String(value ?? "");
  if (typeof value === "string" && /^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildPayrollCsv(slips, detailed = false) {
  if (!slips.length) throw new Error("所選月份沒有已建立的薪資單");
  const header = ["姓名", "實領薪資", "職稱", "年份", "月份", "本薪/時薪薪資", "加項總額", "遲到次數", "遲到分鐘數", "遲到扣款", "請假扣款", "勞保費", "健保費", "扣項總額", "狀態"];
  if (detailed) header.push("人員編號", "加項明細", "其他扣項明細", "請假扣款明細");
  const rows = [...slips].sort((a, b) => number(a.year) - number(b.year) || number(a.month) - number(b.month) || String(a.name || a.uid).localeCompare(String(b.name || b.uid), "zh-TW")).map(p => {
    const s = p.salaryData;
    if (!s || s.net == null) throw new Error(`${p.name || p.uid} ${p.year}/${p.month} 薪資單資料不完整，請先檢查`);
    const d = s.deductions || {};
    const row = [p.name || p.uid, number(s.net), p.role === ROLES.FULLTIME ? "正職" : p.role === ROLES.ADMIN ? "管理員" : "工讀", p.year, p.month, number(s.base), sum(s.plusItems), number(s.lateCount), number(s.lateMinutes), number(d.late), sum(d.leaveDetails), number(d.labor), number(d.health), number(d.total), p.status === "confirmed" ? "已確認" : "未確認"];
    if (detailed) row.push(p.uid, details(s.plusItems), details(s.minusItems), details(d.leaveDetails));
    return row;
  });
  return "\uFEFF" + [header, ...rows].map(row => row.map(csvCell).join(",")).join("\r\n");
}

export function downloadReport({ content, mime, filename }) {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
